/**
 * DG Quick View Drawer — JavaScript Controller
 * ─────────────────────────────────────────────
 * Mirrors cart drawer architecture:
 *   - #dg-quick-view-drawer + .active class
 *   - AJAX fetch via Shopify Sections API
 *   - Reuses addToCart() / refreshCartState() from dg.js
 *
 * Global API:
 *   openQuickView(handle)
 *   closeQuickView()
 *   selectQVVariant(btn)
 *   dgQVQty(delta)
 *   dgQVAddToCart()
 *   dgQVSetImage(url, thumbBtn)
 *   dgQVToggleAccordion(trigger)
 */
(function () {
    'use strict';

    /* ─── State ─── */
    var _currentHandle = null;
    var _qty = 1;
    var _variantId = null;
    var _isLoading = false;
    var _savedScrollY = 0;  // save scroll position for body lock
    var _focusTrapHandler = null;  // stored ref — prevents stacking keydown listeners

    /* ─── DOM refs (resolved lazily) ─── */
    function drawer() { return document.getElementById('dg-quick-view-drawer'); }
    function loading() { return document.getElementById('dg-qv-loading'); }
    function body() { return document.getElementById('dg-qv-body'); }

    /* ══════════════════════════════════════════
       OPEN / CLOSE
    ══════════════════════════════════════════ */

    window.openQuickView = function (handle) {
        if (!handle) return;

        var d = drawer();
        if (!d) return;

        // Reset state
        _qty = 1;
        _variantId = null;
        _currentHandle = handle;

        // Show drawer with loading skeleton
        _showLoading();
        d.classList.add('active');
        d.setAttribute('aria-hidden', 'false');

        // Mobile-proof body scroll lock (html + body required on Android)
        _savedScrollY = window.scrollY;
        document.documentElement.style.top = '-' + _savedScrollY + 'px';
        document.documentElement.classList.add('dg-scroll-locked');
        document.body.classList.add('dg-scroll-locked');

        // Trap focus
        _trapFocus(d);

        // Fetch product section
        _fetchProduct(handle);
    };

    window.closeQuickView = function () {
        var d = drawer();
        if (!d) return;

        // — Remove focus trap before deactivating —
        if (_focusTrapHandler) {
            d.removeEventListener('keydown', _focusTrapHandler);
            _focusTrapHandler = null;
        }

        d.classList.remove('active');
        d.setAttribute('aria-hidden', 'true');

        // Unlock body scroll and restore position
        document.documentElement.classList.remove('dg-scroll-locked');
        document.documentElement.style.top = '';
        document.body.classList.remove('dg-scroll-locked');
        document.body.style.top = '';
        window.scrollTo(0, _savedScrollY);

        _currentHandle = null;
    };

    /* ══════════════════════════════════════════
       FETCH + INJECT
    ══════════════════════════════════════════ */

    function _fetchProduct(handle) {
        if (_isLoading) return;
        _isLoading = true;

        var url = window.Shopify.routes.root + 'products/' + handle + '?sections=dg-quick-view-section';

        fetch(url)
            .then(function (res) {
                if (!res.ok) throw new Error('Quick View fetch failed: ' + res.status);
                return res.json();
            })
            .then(function (data) {
                var html = data['dg-quick-view-section'];
                if (!html) throw new Error('Section not found in response');
                _injectContent(html);
            })
            .catch(function (err) {
                console.error('[DG Quick View]', err);
                _showError();
            })
            .finally(function () {
                _isLoading = false;
            });
    }

    function _injectContent(html) {
        var b = body();
        var l = loading();
        if (!b) return;

        // Parse and inject
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var section = doc.querySelector('.dg-qv-section');
        if (!section) return;

        b.innerHTML = '';
        b.appendChild(section);

        // Read initial variant from hidden input
        var hiddenInput = b.querySelector('#dg-qv-variant-id');
        if (hiddenInput) _variantId = hiddenInput.value;

        // Show body, hide skeleton
        if (l) l.style.display = 'none';
        b.style.display = 'block';

        // Sync counter state with cart (same as product cards)
        var qvForm = b.querySelector('#dg-qv-ajax-form');
        if (qvForm && typeof window.syncCardState === 'function') {
            window.syncCardState(qvForm);
        }

        // Scroll to top
        b.scrollTop = 0;
    }

    function _showLoading() {
        var b = body();
        var l = loading();
        if (b) { b.style.display = 'none'; b.innerHTML = ''; }
        if (l) l.style.display = 'flex';
    }

    function _showError() {
        var b = body();
        var l = loading();
        if (l) l.style.display = 'none';
        if (b) {
            b.style.display = 'block';
            b.innerHTML = '<div style="padding:32px;text-align:center;color:var(--dg-text-muted)">Impossible de charger le produit. <a href="#" onclick="closeQuickView()" style="color:var(--dg-accent)">Fermer</a></div>';
        }
    }

    /* ══════════════════════════════════════════
       VARIANT SELECTION
    ══════════════════════════════════════════ */

    window.selectQVVariantFromSelect = function (selectEl) {
        var b = body();
        if (!b) return;

        var selected = selectEl.options[selectEl.selectedIndex];
        _variantId = selected.value;

        // Update hidden input for cart form
        var hiddenInput = b.querySelector('#dg-qv-variant-id');
        if (hiddenInput) hiddenInput.value = _variantId;

        // Re-sync counter state for new variant
        var qvForm = b.querySelector('#dg-qv-ajax-form');
        if (qvForm && typeof window.syncCardState === 'function') {
            window.syncCardState(qvForm);
        }

        // Update price
        var priceEl = b.querySelector('#dg-qv-price');
        var compareEl = b.querySelector('.dg-qv-price--compare');
        var unitEl = b.querySelector('#dg-qv-unit-price');
       
        if (priceEl) priceEl.textContent = selected.dataset.price;
         priceEl.setAttribute("data-wpd-variant-id", _variantId);
        priceEl.setAttribute("data-wpd-variant-price", Math.round(parseFloat(selected.dataset.price.replace(/[^0-9.]/g, '')) * 100));
        

        var comparePrice = selected.dataset.comparePrice;
        if (comparePrice && comparePrice !== '') {
            if (compareEl) {
                compareEl.textContent = comparePrice;
                compareEl.style.display = '';
            }
            if (priceEl) priceEl.className = 'dg-qv-price--sale';
        } else {
            if (compareEl) compareEl.style.display = 'none';
            if (priceEl) priceEl.className = 'dg-qv-price';
        }

        if (unitEl) {
            var unitPrice = selected.dataset.unitPrice || '';
            unitEl.textContent = unitPrice;
            unitEl.style.display = unitPrice ? '' : 'none';
        }

        // Update main image if variant has one
        var variantImg = selected.dataset.image;
        if (variantImg && variantImg.trim() !== '') {
            var mainImg = b.querySelector('#dg-qv-main-img');
            if (mainImg) {
                mainImg.style.opacity = '0.6';
                mainImg.src = variantImg;
                mainImg.onload = function () { mainImg.style.opacity = '1'; };
            }
        }

        _qty = 1;

        // Update inventory data for backorder warning
        var invQty = selected.dataset.inventoryQty;
        var qvForm = b.querySelector('#dg-qv-ajax-form');
        if (qvForm && invQty !== undefined) {
            qvForm.dataset.inventoryQty = invQty;
            if (typeof window.updateBackorderWarning === 'function') window.updateBackorderWarning(qvForm);
        }

    };

    /* ══════════════════════════════════════════
       QUANTITY
    ══════════════════════════════════════════ */

    var _qvQtyTimer = null;

    window.dgQVQty = function (delta) {
        var b = body();
        if (!b) return;

        _qty = Math.max(1, _qty + delta);

        var qtyVal = b.querySelector('#dg-qv-qty-val');
        if (qtyVal) qtyVal.textContent = _qty;

        // Sync with cart via API only if already in cart
        var atcBtn = b.querySelector('#dg-qv-atc-btn');
        var vid = _variantId || (atcBtn && atcBtn.dataset.variantId);
        if (!vid) return;

        // Check if variant is in cart
        var inCart = false;
        if (window.DG_cartItems && Array.isArray(window.DG_cartItems)) {
            inCart = window.DG_cartItems.some(function (item) {
                return String(item.variant_id) === String(vid) || String(item.id) === String(vid);
            });
        }
        if (!inCart) return; // Not in cart yet — just local qty change

        var btnDec = b.querySelector('.dg-qv-qty-btn[onclick*="-1"]');
        var btnInc = b.querySelector('.dg-qv-qty-btn[onclick*="+1"]');
        function lockQVBtns(locked) {
            if (btnDec) { btnDec.disabled = locked; btnDec.style.opacity = locked ? '0.45' : ''; }
            if (btnInc) { btnInc.disabled = locked; btnInc.style.opacity = locked ? '0.45' : ''; }
        }
        lockQVBtns(true);
        window._dgCartPending = true;

        clearTimeout(_qvQtyTimer);
        var capturedQty = _qty;
        _qvQtyTimer = setTimeout(function () {
            fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: vid.toString(), quantity: capturedQty })
            })
                .then(function (r) { return r.json(); })
                .then(function (cart) {
                    lockQVBtns(false);
                    window._dgCartPending = false;
                    if (cart && typeof window.refreshCartState === 'function') window.refreshCartState();
                    // P1 FIX: Push qty change to Worker + cooldown to guard against polling race
                    if (window.dgCartSync && typeof window.dgCartSync.syncToServer === 'function') {
                        window.dgCartSync.syncToServer();
                    }
                })
                .catch(function (err) {
                    lockQVBtns(false);
                    window._dgCartPending = false;
                    console.error('[DG] QV cart/change error:', err);
                });
        }, 300);
    };

    /* ══════════════════════════════════════════
       ADD TO CART
    ══════════════════════════════════════════ */

    window.dgQVAddToCart = function () {
        var b = body();
        if (!b) return;

        var atcBtn = b.querySelector('#dg-qv-atc-btn');
        var vid = _variantId || (atcBtn && atcBtn.dataset.variantId);
        if (!vid) return;

        // Loading state
        if (atcBtn) {
            atcBtn.disabled = true;
            atcBtn.textContent = 'Ajout en cours…';
        }

        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(vid), quantity: _qty }] })
        })
            .then(function (res) {
                if (!res.ok) throw new Error('Add to cart failed');
                return res.json();
            })
            .then(function () {
                // Success flash
                if (atcBtn) {
                    atcBtn.classList.add('is-added');
                    atcBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg> Ajouté !';
                    setTimeout(function () {
                        atcBtn.classList.remove('is-added');
                        atcBtn.disabled = false;
                        atcBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> Ajouter au panier';
                    }, 1800);
                }

                // Sync cart state (updates header badge + product cards)
                if (typeof refreshCartState === 'function') {
                    refreshCartState();
                }
                // P0 FIX: Push to Worker so Quick View adds sync cross-device
                if (window.dgCartSync && typeof window.dgCartSync.syncToServer === 'function') {
                    window.dgCartSync.syncToServer();
                }
            })
            .catch(function (err) {
                console.error('[DG Quick View] Add to cart error:', err);
                if (atcBtn) {
                    atcBtn.disabled = false;
                    atcBtn.textContent = 'Erreur — Réessayer';
                }
            });
    };

    /* ══════════════════════════════════════════
       GALLERY
    ══════════════════════════════════════════ */

    window.dgQVSetImage = function (url, thumbBtn) {
        var b = body();
        if (!b) return;

        var mainImg = b.querySelector('#dg-qv-main-img');
        if (mainImg) {
            mainImg.style.opacity = '0.6';
            mainImg.src = url;
            mainImg.onload = function () { mainImg.style.opacity = '1'; };
        }

        // Update active thumb
        b.querySelectorAll('.dg-qv-thumb').forEach(function (t) {
            t.classList.remove('is-active');
        });
        if (thumbBtn) thumbBtn.classList.add('is-active');
    };

    /* ══════════════════════════════════════════
       ACCORDION
    ══════════════════════════════════════════ */

    window.dgQVToggleAccordion = function (trigger) {
        var item = trigger.closest('.dg-qv-accordion__item');
        if (!item) return;

        var isOpen = item.classList.contains('is-open');

        // Close all others
        var accordion = item.closest('.dg-qv-accordion');
        if (accordion) {
            accordion.querySelectorAll('.dg-qv-accordion__item.is-open').forEach(function (openItem) {
                openItem.classList.remove('is-open');
                var t = openItem.querySelector('.dg-qv-accordion__trigger');
                if (t) t.setAttribute('aria-expanded', 'false');
            });
        }

        // Toggle this one
        if (!isOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
        }
    };

    /* ══════════════════════════════════════════
       DESCRIPTION EXPAND/COLLAPSE
    ══════════════════════════════════════════ */

    window.dgQVToggleDesc = function (btn) {
        var b = body();
        if (!b) return;
        var descBody = b.querySelector('#dg-qv-desc-body');
        if (!descBody) return;

        var isExpanded = btn.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            descBody.classList.remove('is-expanded');
            btn.setAttribute('aria-expanded', 'false');
            btn.firstChild.textContent = 'Voir plus';
        } else {
            descBody.classList.add('is-expanded');
            btn.setAttribute('aria-expanded', 'true');
            btn.firstChild.textContent = 'Voir moins';
        }
    };

    /* ══════════════════════════════════════════
       FOCUS TRAP
    ══════════════════════════════════════════ */

    function _trapFocus(el) {
        // Remove any previously-registered trap to prevent stacking
        if (_focusTrapHandler) {
            el.removeEventListener('keydown', _focusTrapHandler);
            _focusTrapHandler = null;
        }

        var focusable = el.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        _focusTrapHandler = function (e) {
            if (e.key !== 'Tab') return;
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        el.addEventListener('keydown', _focusTrapHandler);

        // Focus close button
        var closeBtn = el.querySelector('.dg-qv-close');
        if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 50);
    }

    /* ══════════════════════════════════════════
       ESC KEY (extends dg.js handler)
    ══════════════════════════════════════════ */

    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
            var d = drawer();
            if (d && d.classList.contains('active')) {
                closeQuickView();
            }
        }
    });




    /* ══════════════════════════════════════════
       CART EVENT SYNC — re-sync QV counter on any cart update
    ══════════════════════════════════════════ */
    document.addEventListener('dg:cart:updated', function () {
        var d = drawer();
        if (!d || !d.classList.contains('active')) return;
        var b = body();
        if (!b) return;
        var qvForm = b.querySelector('#dg-qv-ajax-form');
        if (qvForm && typeof window.syncCardState === 'function') {
            window.syncCardState(qvForm);
        }
    });

})();
